import React, { useState } from 'react';
import {
  Accordion,
  Alert,
  Button,
  MultiSelect,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { useRevalidator } from 'react-router-dom';
import { z } from 'zod';
import type { ProjectDetail, WorkflowResult } from '@tinypro-audit/contracts';
import { fetchJson } from '../api/client';
import { Evidence } from './ProjectEvidence';
import { fieldLabel } from './project-review-model';
import {
  groupComparisonChecks,
  COMPARISON_LABELS,
  reviewNarrative,
} from '@tinypro-audit/contracts';

const label = (s: string) => s.replaceAll('_', ' ');
type ComparisonCheck =
  WorkflowResult['orders'][number]['stages'][number]['checks'][number];

function ComparisonRows({
  checks,
  expanded = false,
}: {
  checks: ComparisonCheck[];
  expanded?: boolean;
}) {
  const groups = groupComparisonChecks(checks);
  return (
    <Accordion multiple defaultValue={expanded ? groups.map((g) => g.key) : []}>
      {groups.map(({ key, items }) => (
        <Accordion.Item key={key} value={key}>
          <Accordion.Control>
            {items[0].opening_ref ?? items[0].line_ref ?? 'Order'} ·{' '}
            {fieldLabel(items[0].field)} · {label(items[0].outcome)}
            {items.length > 1 && ` · ${items.length} comparisons`}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              {items.map((c, i) => (
                <Stack key={i} gap="xs">
                  <Text size="sm" fw={600}>
                    {COMPARISON_LABELS[c.comparison] ?? label(c.comparison)} ·{' '}
                    {label(c.outcome)}
                  </Text>
                  {c.issue_kind && (
                    <Text size="xs" c="dimmed">
                      {fieldLabel(c.issue_kind)}
                    </Text>
                  )}
                  <Text size="sm">
                    Expected: {c.expected_value ?? 'Unknown'}
                  </Text>
                  <Text size="sm">Found: {c.actual_value ?? 'Unknown'}</Text>
                  <Text size="sm">{reviewNarrative(c.explanation)}</Text>
                  <Text size="sm">{reviewNarrative(c.action)}</Text>
                  <Evidence items={c.evidence} />
                  {c.resolved_by && (
                    <Alert
                      color="blue"
                      title="Current configuration confirmed by later evidence"
                    >
                      <Text size="sm">
                        {reviewNarrative(c.resolved_by.explanation)}
                      </Text>
                      <Evidence items={c.resolved_by.evidence} />
                      <Text size="xs">
                        The original comparison above remains in the history.
                      </Text>
                    </Alert>
                  )}
                  {c.policy_refs.length > 0 && (
                    <Text size="xs">Policy: {c.policy_refs.join(', ')}</Text>
                  )}
                </Stack>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}

export function ComparisonChecks({
  checks,
  expanded = true,
}: {
  checks: ComparisonCheck[];
  expanded?: boolean;
}) {
  const matches = checks.filter((c) => c.outcome === 'match');
  const remaining = checks.filter(
    (c) => c.outcome !== 'match' && c.attention !== 'information',
  );
  const notes = checks.filter(
    (c) => c.outcome !== 'match' && c.attention === 'information',
  );
  return (
    <Stack gap="sm">
      {!!remaining.length && (
        <ComparisonRows checks={remaining} expanded={expanded} />
      )}
      {!!notes.length && (
        <Accordion>
          <Accordion.Item value="notes">
            <Accordion.Control>
              Informational comparisons · {notes.length}
            </Accordion.Control>
            <Accordion.Panel>
              <ComparisonRows checks={notes} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
      {!!matches.length && (
        <Accordion>
          <Accordion.Item value="matches">
            <Accordion.Control>
              Matched comparisons · {matches.length}
            </Accordion.Control>
            <Accordion.Panel>
              <ComparisonRows checks={matches} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </Stack>
  );
}
export function HoldEditor({
  data,
  order,
}: {
  data: ProjectDetail;
  order: ProjectDetail['workflow']['orders'][number];
}) {
  const [status, setStatus] = useState(order.hold_status),
    [note, setNote] = useState(''),
    [refs, setRefs] = useState<string[]>([]),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const revalidator = useRevalidator();
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await fetchJson(
        `/api/projects/${data.project.st_project_id}/orders/${order.id}/hold`,
        {
          method: 'POST',
          body: JSON.stringify({
            status,
            note,
            expected_updated_at: order.updated_at,
            evidence: refs.map((id) => ({ kind: 'document', id })),
          }),
        },
        z.unknown(),
      );
      setNote('');
      await revalidator.revalidate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Accordion>
      <Accordion.Item value="hold">
        <Accordion.Control>
          Manufacturer hold: {label(order.hold_status)}
        </Accordion.Control>
        <Accordion.Panel>
          <Stack>
            <Text size="sm">
              Record actions taken. Saving here does not contact the
              manufacturer, request a hold, authorize production, or release an
              order.
            </Text>
            <Select
              label="Recorded hold status"
              value={status}
              onChange={(s) => setStatus(s ?? 'unknown')}
              data={[
                'unknown',
                'recommended',
                'requested',
                'confirmed',
                'released',
              ].map((s) => ({ value: s, label: label(s) }))}
            />
            <MultiSelect
              label="Written request / confirmation / release"
              value={refs}
              onChange={setRefs}
              data={data.documents.map((d) => ({
                value: d.id,
                label: String(d.metadata.original_filename ?? d.id),
              }))}
            />
            <Textarea
              label="Action and confirmation details"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
            />
            {error && <Alert color="red">{error}</Alert>}
            <Button
              disabled={!note.trim()}
              loading={busy}
              onClick={() => void save()}
            >
              Record action
            </Button>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
